import React, { useState, useEffect } from 'react';
import { History, ArrowDownCircle, ArrowUpCircle, Package, Calendar } from 'lucide-react';
import api, { API_URL } from '../../services/api';

function HistoriqueOrOuvrier({ ouvrierId, ouvrierNom }) {
  const [historique, setHistorique] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (ouvrierId) {
      loadHistorique();
    }
  }, [ouvrierId]);

  const loadHistorique = async () => {
    try {
      const response = await api.get(`${API_URL}/ouvriers/${ouvrierId}/historique-or`);
      setHistorique(response.data);
    } catch (error) {
      console.error('Erreur chargement historique:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-gray-500">Chargement de l'historique...</div>
      </div>
    );
  }

  if (!historique) {
    return (
      <div className="text-center py-8 text-gray-500">
        Aucun historique disponible
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white p-4 rounded-lg">
        <div className="flex items-center space-x-3">
          <History className="h-6 w-6" />
          <div>
            <h3 className="text-lg font-bold">Historique des mouvements d'or</h3>
            <p className="text-sm opacity-90">{ouvrierNom}</p>
          </div>
        </div>
      </div>

      {/* Historique par fiche de fabrication */}
      {historique.historique_par_fiche && historique.historique_par_fiche.length > 0 && (
        <div className="space-y-4">
          <h4 className="font-bold text-gray-800 flex items-center space-x-2">
            <Package className="h-5 w-5 text-blue-600" />
            <span>Mouvements par fiche de fabrication</span>
          </h4>

          {historique.historique_par_fiche.map((fiche) => (
            <div key={fiche.ordre_id} className="bg-white border-2 border-blue-200 rounded-lg overflow-hidden">
              {/* En-tête de la fiche */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 border-b-2 border-blue-200">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-bold text-blue-900">
                      Ordre #{fiche.numero_ordre} - {fiche.type_bijou}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      {fiche.reference_bijou}
                      {fiche.client_nom && ` - ${fiche.client_nom}`}
                    </div>
                    <div className="text-sm font-semibold text-purple-700 mt-1">
                      Mission : {fiche.mission}
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                    fiche.statut === 'termine' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {fiche.statut === 'termine' ? 'Terminé' : 'En cours'}
                  </span>
                </div>

                {/* Résumé des poids */}
                <div className="grid grid-cols-3 gap-4 mt-3 pt-3 border-t border-blue-200">
                  <div className="text-center">
                    <div className="text-xs text-gray-600">Or donné</div>
                    <div className="text-lg font-bold text-blue-600">
                      {fiche.poids_or_donne.toFixed(2)}g
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-gray-600">Bijou fini</div>
                    <div className="text-lg font-bold text-green-600">
                      {fiche.poids_bijou_fini ? fiche.poids_bijou_fini.toFixed(2) : '0.00'}g
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-gray-600">Or restant</div>
                    <div className="text-lg font-bold text-amber-600">
                      {fiche.or_restant ? fiche.or_restant.toFixed(2) : '0.00'}g
                    </div>
                  </div>
                </div>
              </div>

              {/* Mouvements de cette fiche */}
              <div className="p-4 space-y-2">
                {fiche.mouvements.map((mouvement) => (
                  <div 
                    key={mouvement.id} 
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      mouvement.type_mouvement === 'entree' 
                        ? 'bg-green-50 border border-green-200' 
                        : 'bg-red-50 border border-red-200'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      {mouvement.type_mouvement === 'entree' ? (
                        <ArrowDownCircle className="h-5 w-5 text-green-600" />
                      ) : (
                        <ArrowUpCircle className="h-5 w-5 text-red-600" />
                      )}
                      <div>
                        <div className="font-semibold text-sm">
                          {mouvement.type_mouvement === 'entree' ? 'Entrée' : 'Sortie'}
                        </div>
                        <div className="text-xs text-gray-600">{mouvement.notes}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`font-bold ${
                        mouvement.type_mouvement === 'entree' ? 'text-green-700' : 'text-red-700'
                      }`}>
                        {mouvement.type_mouvement === 'entree' ? '+' : '-'}{mouvement.quantite.toFixed(2)}g
                      </div>
                      <div className="text-xs text-gray-500 flex items-center space-x-1">
                        <Calendar className="h-3 w-3" />
                        <span>{new Date(mouvement.date_mouvement).toLocaleDateString('fr-FR')}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Mouvements sans fiche */}
      {historique.mouvements_sans_fiche && historique.mouvements_sans_fiche.length > 0 && (
        <div className="space-y-4">
          <h4 className="font-bold text-gray-800">Autres mouvements</h4>
          <div className="bg-white border-2 border-gray-200 rounded-lg p-4 space-y-2">
            {historique.mouvements_sans_fiche.map((mouvement) => (
              <div 
                key={mouvement.id} 
                className={`flex items-center justify-between p-3 rounded-lg ${
                  mouvement.type_mouvement === 'entree' 
                    ? 'bg-green-50 border border-green-200' 
                    : 'bg-red-50 border border-red-200'
                }`}
              >
                <div className="flex items-center space-x-3">
                  {mouvement.type_mouvement === 'entree' ? (
                    <ArrowDownCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <ArrowUpCircle className="h-5 w-5 text-red-600" />
                  )}
                  <div>
                    <div className="font-semibold text-sm">
                      {mouvement.type_mouvement === 'entree' ? 'Entrée' : 'Sortie'}
                    </div>
                    <div className="text-xs text-gray-600">{mouvement.notes || mouvement.reference}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`font-bold ${
                    mouvement.type_mouvement === 'entree' ? 'text-green-700' : 'text-red-700'
                  }`}>
                    {mouvement.type_mouvement === 'entree' ? '+' : '-'}{mouvement.quantite.toFixed(2)}g
                  </div>
                  <div className="text-xs text-gray-500 flex items-center space-x-1">
                    <Calendar className="h-3 w-3" />
                    <span>{new Date(mouvement.date_mouvement).toLocaleDateString('fr-FR')}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {historique.historique_par_fiche.length === 0 && historique.mouvements_sans_fiche.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          Aucun mouvement d'or enregistré pour cet ouvrier
        </div>
      )}
    </div>
  );
}

export default HistoriqueOrOuvrier;
